import React from 'react';
import {
  Box,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Icon,
  IconButton,
  Drawer,
  useTheme
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import { DrawerStyled, DrawerHeader, MenuItemContainer } from '../StyledComponents';
import { IModulo } from '../types';
import { useTranslation } from '../../../commons/useTranslasion';
import { DRAWER_WIDTH, COLLAPSED_DRAWER_WIDTH, HEADER_HEIGHT } from '../constants';

interface SidebarProps {
  open: boolean;
  isMobile: boolean;
  menuItens: IModulo[];
  path: string;
  handleDrawerClose: () => void;
  handleMobileMenuToggle: () => void;
  handleMenuClick: (url: string) => void;
  handleLogout: () => void;
}

/**
 * Função auxiliar para determinar a cor do menu ativo
 */
const getMenuColor = () => {
  return '#08467c'; // Cor azul principal usada como cor primária
};

/**
 * Componente da barra lateral de navegação
 */
const Sidebar: React.FC<SidebarProps> = ({
  open,
  isMobile,
  menuItens,
  path,
  handleDrawerClose,
  handleMobileMenuToggle,
  handleMenuClick,
  handleLogout
}) => {
  const { translate } = useTranslation();
  const theme = useTheme();
  
  const renderMenuItems = () => (
    <>
      <List sx={{ mt: isMobile ? `${HEADER_HEIGHT}px` : 0 }}>
        {menuItens.map((modulo) => {
          const isActive = modulo.link === '/' 
            ? path === '/' || path === '/dashboard' // Dashboard ativo apenas na página inicial
            : path.startsWith(modulo.link); // Outras páginas mantêm a lógica original
          return (
            <ListItemButton
              key={modulo.link}
              onClick={() => handleMenuClick(modulo.link)}
              selected={isActive}
              sx={{
                mb: 1,
                borderRadius: '8px',
                mx: 1,
                backgroundColor: isActive ? 'rgba(25, 118, 210, 0.08)' : 'transparent',
                color: isActive ? '#08467c' : 'inherit',
                '&:hover': {
                  backgroundColor: 'rgba(25, 118, 210, 0.04)',
                },
              }}
            >
              <ListItemIcon sx={{ minWidth: 40, color: isActive ? '#08467c' : 'inherit' }}>
                <Icon>{modulo.icon}</Icon>
              </ListItemIcon>
              <ListItemText primary={translate(modulo.nome)} />
            </ListItemButton>
          );
        })}
      </List>

      <Divider sx={{ my: 1 }} />

      {/* Botão de logout - área de clique reduzida */}
      <Box sx={{ px: 1, mt: 1, mb: 2 }}>
        <ListItemButton
          onClick={handleLogout}
          sx={{
            borderRadius: '8px',
            py: 1, // Reduzindo o padding vertical
            width: 'auto',
            maxWidth: '150px', // Limitar largura máxima
            display: 'flex',
            justifyContent: 'flex-start',
            '&:hover': {
              backgroundColor: 'rgba(211, 47, 47, 0.04)',
            },
          }}
        >
          <ListItemIcon sx={{ minWidth: 36, color: '#d32f2f' }}>
            <ExitToAppIcon />
          </ListItemIcon>
          <ListItemText primary={translate('sair')} />
        </ListItemButton>
      </Box>
    </>
  );

  // No modo mobile, usamos o Drawer diretamente do MUI para garantir o comportamento correto
  if (isMobile) {
    return (
      <Drawer
        open={open}
        onClose={handleMobileMenuToggle}
        variant="temporary"
        anchor="left"
        ModalProps={{
          keepMounted: true,
        }}
        PaperProps={{
          style: {
            width: DRAWER_WIDTH,
            backgroundColor: '#f3f3f3',
          }
        }}
        sx={{
          display: 'block',
          '& .MuiDrawer-paper': {
            width: DRAWER_WIDTH,
            boxSizing: 'border-box',
            zIndex: theme.zIndex.drawer + 1,
          }
        }}
      >

              {renderMenuItems()}
      </Drawer>
    );
  }
  
  // No modo desktop, usamos o DrawerStyled com variante permanente
  return (
    <DrawerStyled
      variant="permanent"
      open={open}
      isMobile={false}
    >
      <Box sx={{ overflow: 'hidden' }}>
        {/* Botão de expandir/colapsar do menu (apenas desktop) */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            p: 1
          }}
        >
          <IconButton
            onClick={handleDrawerClose}
            sx={{
              width: '36px',
              height: '36px',
              backgroundColor: 'rgba(25, 118, 210, 0.04)',
              '&:hover': {
                backgroundColor: 'rgba(25, 118, 210, 0.08)',
              },
            }}
          >
            {!open ? <ChevronRightIcon /> : <ChevronLeftIcon />}
          </IconButton>
        </Box>

        {/* Pequeno espaçamento após o botão de expandir/colapsar */}
        <Box sx={{ height: '4px' }} />

        {/* Itens do menu */}
        <List sx={{ px: 2, mt: isMobile ? 8 : 0.5 }}>
          {menuItens.map((menuItem, index) => (
            <MenuItemContainer
              key={index}
              disablePadding
              ativo={path === menuItem.link ? 'true' : undefined}
              open={open || isMobile}
              onClick={() => handleMenuClick(menuItem.link)}
              data-testid={`menu-item-${index}`}
            >
              <ListItemButton
                sx={{
                  py: 1.5,
                  px: !open && !isMobile ? 1 : 2,
                  justifyContent: !open && !isMobile ? 'center' : 'flex-start',
                  alignItems: 'center',
                  minWidth: 0,
                  position: 'relative',
                  transition: 'all 0.2s ease',
                  ...(open || isMobile) && {
                    display: 'flex',
                    justifyContent: 'center',
                  },
                  '&:hover': {
                    backgroundColor: 'transparent',
                  },
                  ...!open && !isMobile && {
                    '& .MuiTouchRipple-root': {
                      display: 'none',
                    }
                  }
                }}
              >
                <Box
                  sx={{
                    position: 'relative',
                    width: !open && !isMobile ? '100%' : 36,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: !open && !isMobile ? 0 : 36,
                      color: path === menuItem.link ? getMenuColor() : 'gray',
                      justifyContent: 'center',
                      width: !open && !isMobile ? 'auto' : 36,
                      margin: 0,
                      display: 'flex',
                      alignItems: 'center',
                      zIndex: 1,
                    }}
                  >
                    <Icon>{menuItem.icon}</Icon>
                  </ListItemIcon>
                </Box>
                {(open || isMobile) && (
                  <ListItemText
                    primary={menuItem.nome}
                    sx={{
                      margin: 0,
                      padding: 0,
                      '& .MuiTypography-root': {
                        textAlign: 'center',
                      }
                    }}
                    primaryTypographyProps={{
                      style: {
                        color: path === menuItem.link ? getMenuColor() : '#585858',
                        fontWeight: path === menuItem.link ? 'bold' : 'normal',
                      },
                    }}
                  />
                )}
              </ListItemButton>
            </MenuItemContainer>
          ))}
        </List>

        <Divider sx={{ my: 2 }} />

        {/* Botão de logout */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            px: 2
          }}
        >
          <MenuItemContainer
            disablePadding
            open={open || isMobile}
          >
            <ListItemButton
              onClick={handleLogout}
              sx={{
                py: 1.5,
                px: !open && !isMobile ? 1 : 2,
                justifyContent: !open && !isMobile ? 'center' : 'center',
                position: 'relative',
                borderRadius: '10px',
                '&:hover': {
                  backgroundColor: 'rgba(25, 118, 210, 0.04)',
                },
                ...!open && !isMobile && {
                  '& .MuiTouchRipple-root': {
                    display: 'none',
                  }
                }
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: !open && !isMobile ? 0 : 36,
                    color: 'gray',
                    justifyContent: 'center'
                  }}
                >
                  <Icon>logout</Icon>
                </ListItemIcon>

                {(open || isMobile) && (
                  <ListItemText
                    primary={translate('sair')}
                    sx={{
                      margin: 0,
                      padding: 0,
                      '& .MuiTypography-root': {
                        textAlign: 'center',
                        color: '#585858',
                      }
                    }}
                  />
                )}
              </Box>
            </ListItemButton>
          </MenuItemContainer>
        </Box>
      </Box>
    </DrawerStyled>
  );
};

export default Sidebar;
